import type {
  GeneratedImageResult,
  ImageGenParams,
  ImageProvider,
  ImageProviderConfig,
} from "@roqeet/core";

/**
 * OpenAI 이미지 생성 어댑터 — 결정 #19(ImageProvider 추상화).
 *
 * OpenAI Images API(`/v1/images/generations`)를 호출한다. `response_format`을
 * b64_json으로 받아 곧장 Blob으로 디코드 → 콘텐츠 주소화 저장(결정 #18)로 이어진다.
 * gpt-image-1은 b64_json만 반환하므로 항상 base64 경로를 쓴다.
 */
export class OpenAiImageProvider implements ImageProvider {
  readonly kind = "openai";
  readonly supportsProgress = false;

  async generate(
    params: ImageGenParams,
    config: ImageProviderConfig,
    _onProgress?: (pct: number, previewBlob?: Blob) => void,
  ): Promise<GeneratedImageResult[]> {
    const base = (config.endpoint ?? "https://api.openai.com/v1").replace(/\/+$/, "");
    const model = config.defaultModel ?? "gpt-image-1";

    const headers: Record<string, string> = {
      "content-type": "application/json",
      ...(config.extraHeaders ?? {}),
    };
    // 키는 호출부(액션)가 볼트에서 해석해 extraHeaders.authorization로 주입하거나
    // keyRef를 풀어 넘긴다. 여기서는 config.keyRef가 평문 키라고 가정하지 않는다.

    const body: Record<string, unknown> = {
      model,
      prompt: params.prompt,
      n: params.count ?? 1,
    };
    if (params.width && params.height) body["size"] = `${params.width}x${params.height}`;
    // dall-e류는 response_format 지원, gpt-image-1은 항상 b64.
    if (model.startsWith("dall-e")) body["response_format"] = "b64_json";

    const init: RequestInit = { method: "POST", headers, body: JSON.stringify(body) };
    if (params.signal) init.signal = params.signal;

    const res = await fetch(`${base}/images/generations`, init);
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`이미지 생성 실패 (HTTP ${res.status}): ${text.slice(0, 400)}`);
    }
    const json = (await res.json()) as { data?: { b64_json?: string; url?: string }[] };
    const items = json.data ?? [];
    const results: GeneratedImageResult[] = [];

    for (const item of items) {
      let blob: Blob;
      if (item.b64_json) {
        blob = base64ToBlob(item.b64_json, "image/png");
      } else if (item.url) {
        const r = await fetch(item.url);
        blob = await r.blob();
      } else {
        continue;
      }
      results.push({
        blob,
        provenance: {
          provider: config.providerId ?? this.kind,
          model,
          prompt: params.prompt,
          ...(params.negativePrompt ? { negativePrompt: params.negativePrompt } : {}),
          ...(params.seed != null ? { seed: params.seed } : {}),
          ...(params.width ? { width: params.width } : {}),
          ...(params.height ? { height: params.height } : {}),
        },
      });
    }
    return results;
  }
}

/** base64 → Blob(브라우저). atob은 web/RN(polyfill)/Node20 공통. */
export function base64ToBlob(b64: string, mime: string): Blob {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}
